/**
 * Shared machinery for the god-mode write audit shims
 * (firestoreAudited.js and storageAudited.js).
 *
 * God mode signs the app into a real session as another dashboard user. The
 * rules let that session write what the target could write — but only with an
 * audit entry in the same batch (firestore.rules, godmodeAudited()). This
 * module knows how to tell whether the current session is a god-mode one, how
 * the rules expect audit entries to be named, and how to turn a write into a
 * bounded, storable record of itself.
 *
 * Nothing here is imported by pages. Pages import 'firebase/firestore' and
 * 'firebase/storage' as they always have; vite.config.js aliases those
 * specifiers to the shims, and the shims import this.
 */

import { getAuth } from 'firebase/auth';
import {
  doc as realDoc,
  serverTimestamp,
  FieldValue,
  Timestamp,
  GeoPoint,
  DocumentReference,
  Bytes,
} from '@firebase/firestore';

/** Top-level collection the sessions and their audit entries live in. */
export const SESSIONS = 'godmode_sessions';

/**
 * Target writes per audited batch. Rules allow 20 document-access calls per
 * batched write; an audited write costs the pointer, its entry, and the
 * target's role lookups (users + org_managers). 4 keeps the worst case under
 * the limit even if calls are not deduplicated by document. Larger god-mode
 * batches are split into several commits — each still atomic with its own
 * entries, but no longer atomic with each other. Normal sessions are not
 * chunked.
 */
export const GODMODE_BATCH_CHUNK = 4;

/** Per-value cap when recording before/after; longer strings are cut. */
const MAX_STRING = 2000;
/** Whole before/after payload cap (approximate JSON size). */
const MAX_PAYLOAD = 20000;
const MAX_DEPTH = 6;
const MAX_ARRAY = 200;

/** 'booths/b1' → 'booths~b1'. The rules build the same key from wildcards. */
export function keyForPath(path) {
  return path.split('/').join('~');
}

/** A fresh per-batch nonce. Uniqueness within one session is all it needs. */
export function newNonce() {
  const bytes = new Uint8Array(6);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Where the viewer is in the app, for the audit row. Null outside a browser. */
export function currentRoute() {
  if (typeof window === 'undefined' || !window.location) return null;
  return `${window.location.pathname}${window.location.search || ''}`;
}

/**
 * Is this app currently signed into a god-mode session?
 *
 * Read from the ID token every time rather than from React state: the shim
 * runs underneath every page, and a write issued in the moment between the
 * session swap and the state update must still be audited. getIdTokenResult
 * decodes the cached token locally; it only goes to the network when the
 * token is due for refresh, which the write itself would have triggered.
 *
 * Returns null for a normal session (the shims then pass straight through),
 * or { uid, actor, sessionId } for a god-mode one. A god-mode token with no
 * session id predates write auditing: `sessionId` is null and the shims
 * refuse the write, because there is nowhere to record it.
 */
export async function resolveGodmodeContext(app) {
  let user;
  try {
    user = getAuth(app).currentUser;
  } catch {
    return null;
  }
  if (!user) return null;
  try {
    const { claims } = await user.getIdTokenResult();
    if (claims?.godmode !== true) return null;
    return {
      uid: user.uid,
      actor: typeof claims.godmode_actor === 'string' ? claims.godmode_actor : null,
      sessionId: typeof claims.godmode_session === 'string' && claims.godmode_session
        ? claims.godmode_session
        : null,
    };
  } catch (err) {
    console.error('[godmode] could not read ID token claims for write audit:', err);
    // Fail closed: treat as god mode with no session, so the write is refused
    // rather than performed unrecorded by a session we could not identify.
    return { uid: user.uid, actor: null, sessionId: null };
  }
}

export class GodmodeAuditUnavailable extends Error {
  constructor() {
    super('This god-mode session predates write auditing and is read-only. '
      + 'Exit god mode and start a new session to make changes.');
    this.name = 'GodmodeAuditUnavailable';
    this.code = 'godmode/audit-unavailable';
  }
}

export function assertAuditable(ctx) {
  if (!ctx.sessionId) throw new GodmodeAuditUnavailable();
}

/** The per-batch pointer the rules read the nonce from. */
export function pointerRef(db, ctx) {
  return realDoc(db, SESSIONS, ctx.sessionId, 'meta', 'batch');
}

/** The entry document for one target write in one batch. */
export function entryRef(db, ctx, nonce, key) {
  return realDoc(db, SESSIONS, ctx.sessionId, 'writes', `${nonce}~${key}`);
}

/** The entry document for one Storage object write (no batch, no nonce). */
export function storageEntryRef(db, ctx, method, key) {
  return realDoc(db, SESSIONS, ctx.sessionId, 'writes', `storage~${method}~${key}`);
}

// ---------------------------------------------------------------------------
// Turning write data into a record of itself
// ---------------------------------------------------------------------------

/**
 * Top-level field name of an update key. updateDoc accepts dotted paths
 * ('print_settings.lowMediaThreshold') and FieldPath objects; the rules'
 * affectedKeys() reports top-level names, so that is what the entry lists.
 */
export function topLevelKey(key) {
  if (typeof key === 'string') return key.split('.')[0];
  // FieldPath: the first segment is the top-level name. The property is
  // internal to the SDK, so fall back to a sentinel that will not match
  // anything — the write is then denied rather than under-reported.
  const segments = key?._internalPath?.segments;
  if (Array.isArray(segments) && segments.length) return String(segments[0]);
  return '__unknown_field_path__';
}

/**
 * The data object an updateDoc call describes, whichever form it used:
 * updateDoc(ref, { ... }) or updateDoc(ref, field, value, field, value, ...).
 */
export function updateArgsToObject(args) {
  if (args.length === 1 && args[0] && typeof args[0] === 'object' && !(args[0] instanceof FieldValue)) {
    return args[0];
  }
  const out = {};
  for (let i = 0; i + 1 < args.length; i += 2) {
    const k = typeof args[i] === 'string' ? args[i] : topLevelKey(args[i]);
    out[k] = args[i + 1];
  }
  return out;
}

/** Distinct top-level field names touched by an update object. */
export function changedKeysOf(data) {
  return Array.from(new Set(Object.keys(data || {}).map(topLevelKey)));
}

/**
 * A storable, bounded copy of a value. Sentinels (serverTimestamp, increment,
 * arrayUnion, deleteField…) become `{ __fieldValue: name }` — they cannot be
 * nested in a create and their operands are private. References become their
 * path. Bytes become a size. Timestamps, dates and geopoints are storable as
 * they are.
 */
export function sanitizeValue(value, depth = 0) {
  if (value === undefined || value === null) return null;
  if (value instanceof FieldValue) return { __fieldValue: value._methodName || 'FieldValue' };
  if (value instanceof Timestamp || value instanceof Date || value instanceof GeoPoint) return value;
  if (value instanceof DocumentReference) return { __ref: value.path };
  if (value instanceof Bytes) return { __bytes: value.toUint8Array().length };
  if (typeof value === 'string') {
    if (value.startsWith('data:')) return `<data url, ${value.length} chars>`;
    return value.length > MAX_STRING ? `${value.slice(0, MAX_STRING)}… (+${value.length - MAX_STRING})` : value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) {
    if (depth >= MAX_DEPTH) return `<array of ${value.length}>`;
    return value.slice(0, MAX_ARRAY).map((v) => sanitizeValue(v, depth + 1));
  }
  if (typeof value === 'object') {
    if (depth >= MAX_DEPTH) return `<object with ${Object.keys(value).length} keys>`;
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (v === undefined) continue;
      out[k] = sanitizeValue(v, depth + 1);
    }
    return out;
  }
  return String(value);
}

/**
 * Update data with dotted paths expanded into nested maps, so the recorded
 * `after` reads like the document will, and no field name carries a dot.
 */
export function expandDotted(data) {
  const out = {};
  for (const [rawKey, value] of Object.entries(data || {})) {
    if (typeof rawKey !== 'string' || !rawKey.includes('.')) {
      out[rawKey] = value;
      continue;
    }
    const parts = rawKey.split('.');
    let cursor = out;
    parts.forEach((part, i) => {
      if (i === parts.length - 1) {
        cursor[part] = value;
      } else {
        if (!cursor[part] || typeof cursor[part] !== 'object') cursor[part] = {};
        cursor = cursor[part];
      }
    });
  }
  return out;
}

/** The subset of `source` under the named top-level keys, sanitised and capped. */
export function snapshotFields(source, keys) {
  if (!source) return null;
  const picked = {};
  keys.forEach((k) => {
    if (k in source) picked[k] = source[k];
  });
  return capPayload(sanitizeValue(picked));
}

function approxSize(value) {
  try {
    return JSON.stringify(value).length;
  } catch {
    return MAX_PAYLOAD + 1;
  }
}

/** Replace an oversized payload with its shape, so a row can never balloon. */
export function capPayload(value) {
  if (value && typeof value === 'object' && approxSize(value) > MAX_PAYLOAD) {
    return { __truncated: true, keys: Object.keys(value) };
  }
  return value;
}

/**
 * The audit entry for one Firestore write. `op` is what the rules will see
 * as request.method; `changed` must list every top-level key the write
 * touches (over-reporting is allowed, omitting is denied).
 */
export function buildEntry(ctx, { op, path, changed, before, after }) {
  return {
    at: serverTimestamp(),
    session: ctx.sessionId,
    actor: ctx.actor,
    uid: ctx.uid,
    op,
    key: keyForPath(path),
    path,
    changed,
    before: before ?? null,
    after: after ?? null,
    route: currentRoute(),
    source: 'client',
  };
}
